import React, { useEffect, useMemo } from "react";
import { useSupervisorEmailsFromRole } from "@hooks/programs/useSupervisorEmailsFromRole";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonContent,
  IonSpinner,
  IonButton,
} from "@ionic/react";
import HeaderTitle from "@components/common/HeaderTitle";
import DataController from "@controllers/DataController";
import { Capacitor } from "@capacitor/core";
import { Icon_Refresh } from "@assets/images";
import { useProgramData } from "@hooks/data/useProgramData";
import { useRefreshProgramData } from "@hooks/programs/useRefreshProgramData";
import { useHistory, useParams } from "react-router";
import { EvidenceDefinition, IEvidence, WPBAForm } from "@interfaces/Interfaces";
import { userRolesSelector } from "@state/UserState";
import { evidenceAtom } from "@state/State";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userAtom } from "@state/State";
import { useRecoilValueLoadable } from "recoil";
import { IUser } from "@interfaces/Interfaces";
import * as ProgramUtils from "@utils/programUtils";
import _ from "lodash";
import ProgramInfoCard from "@components/home/ProgramInfoCard";
import ProgramEvidenceCard from "@components/programs/ProgramEvidenceCard";
import WPBAFormButton from "@components/programs/WPBAFormButton";
import { EventRegister } from "react-native-event-listeners";
import * as FirebaseService from "@controllers/FirebaseService";
import {
  normalizeSupervisorEmailRows,
  supervisorAutofillSelectionAtom,
  supervisorEmailsFromRoleAtom,
} from "@state/EvidenceState";

const WPBAPage: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ programId: string }>();

  const { isRefreshingProgramData, refreshUserProgramData } = useRefreshProgramData();
  const { userPrograms } = useProgramData();

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const allEvidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const allUserRoles = useRecoilValue(userRolesSelector);
  const setSupervisorEmailsFromRole = useSetRecoilState(supervisorEmailsFromRoleAtom);
  const setSupervisorAutofillSelection = useSetRecoilState(supervisorAutofillSelectionAtom);

  const program = userPrograms.find((item) => item.ID === params.programId)!;
  const programRoles = allUserRoles.find((role) => role.programID === params.programId);

  const { supervisorEmails } = useSupervisorEmailsFromRole(params.programId ?? "", "ALL", true);

  useEffect(() => {
    if (supervisorEmails === undefined) {
      return;
    }
    setSupervisorEmailsFromRole(normalizeSupervisorEmailRows(supervisorEmails));
  }, [supervisorEmails, setSupervisorEmailsFromRole]);

  let userRoles: string[] = [];

  if (programRoles) {
    userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
  }

  let programEvidence: IEvidence[] = [];

  if (allEvidence.state === "hasValue" && allEvidence.contents) {
    programEvidence = allEvidence.contents.filter((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const id = evidenceJSON?.programInfo?.programID || "";

      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return id === program?.ID && onHoldReset !== 1 && evidence.draft !== true;
    });

    const formEvidenceDefinitions: EvidenceDefinition[] =
      program["WPBA Forms"]?.flatMap((form) => form["Evidence Definitions"] ?? []) ?? [];

    programEvidence = programEvidence.filter((evidence) => {
      return formEvidenceDefinitions.some(
        (item) => item.ID === evidence.evidenceJSON.programInfo.programEvidenceDefinitionID
      );
    });

    programEvidence = _.orderBy(programEvidence, "date", "desc");
  }

  const wpbaForms = _.sortBy(program["WPBA Forms"] ?? [], "Name");

  const wpbaFormEvidence: IEvidence[] = useMemo(() => {
    const formEvidenceDefinitions = wpbaForms.flatMap((form) => form["Evidence Definitions"]?.[0]?.ID ?? []);

    return programEvidence.filter((evidence) => {
      return formEvidenceDefinitions.some(
        (item) => item === evidence.evidenceJSON.programInfo.programEvidenceDefinitionID
      );
    });
  }, [programEvidence, wpbaForms]);

  const wpbaSuperviseeEvidence: IEvidence[] = useMemo(() => {
    return wpbaFormEvidence.filter((evidence: IEvidence) => {
      return !evidence.evidenceJSON.programInfo.supervisorCopy;
    });
  }, [wpbaFormEvidence]);

  const wpbaFormSupervisorEvidence: IEvidence[] = useMemo(() => {
    return wpbaFormEvidence.filter((evidence: IEvidence) => {
      return evidence.evidenceJSON.programInfo.supervisorCopy;
    });
  }, [wpbaFormEvidence]);

  const evidenceCardClicked = (item: IEvidence): void => {
    history.push(`/dashboard/program/${params.programId}/read/${item.id}`);
  };

  const addEvidence = async (form: WPBAForm): Promise<void> => {
    try {
      // if (supervisorEmails === undefined) {
      //   const supervisorEmailsRaw = await fetchSupervisorEmailsFromRole();
      //   if (supervisorEmailsRaw !== undefined) {
      //     setSupervisorEmailsFromRole(normalizeSupervisorEmailRows(supervisorEmailsRaw));
      //   }
      // }
      setSupervisorAutofillSelection(null);

      const usesAdditionalEvidenceDefinitions = program.UsesAdditionalEvidenceDefinitions;
      const evidenceDefinitions = form["Evidence Definitions"] ?? [];
      const mandatoryStandards = ProgramUtils.getMandatoryStandards(user.contents, program);
      const optionalStandards = ProgramUtils.getOptionalStandards(user.contents, program);

      const evidenceDefinition = evidenceDefinitions.find(
        (item) => item.EvidenceSelectionType === "wpba-supervision-form"
      )!;
      const learningObjectives = ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]);

      const data = {
        program: program,
        ...(evidenceDefinitions.length > 0 &&
          usesAdditionalEvidenceDefinitions && {
            evidenceDefinitions: evidenceDefinition,
          }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(learningObjectives.length > 0 && { learningObjectives }),
        wpbaForm: form,
      };

      FirebaseService.logEvent("program_add_enhanced_evidence", {
        program: program.Name,
      });

      EventRegister.emit("evidence/add-program-evidence", data);
    } catch (error) {
      console.error("Error adding evidence", error);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref={`/dashboard/program/${params.programId}`}
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{"WPBA Forms"}</HeaderTitle>
          <IonButtons slot="end">
            <IonButton
              disabled={isRefreshingProgramData}
              className="header-button"
              mode="ios"
              onClick={refreshUserProgramData}
            >
              {isRefreshingProgramData ? (
                <IonSpinner className="h-[20px] w-[44px] text-white" />
              ) : (
                <img
                  src={Icon_Refresh}
                  className="headerIcon"
                />
              )}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <ProgramInfoCard title="WPBA Forms">
          {wpbaForms.map((form) => (
            <WPBAFormButton
              key={form.ID}
              form={form}
              buttonPressed={() => addEvidence(form)}
              programEvidence={programEvidence}
            />
          ))}
        </ProgramInfoCard>
        <ProgramInfoCard
          title="Authored as clinician"
          subTitle={wpbaSuperviseeEvidence.length === 0 ? "Any evidence added will appear below." : undefined}
        >
          {wpbaSuperviseeEvidence.map((item) => (
            <ProgramEvidenceCard
              key={item.id}
              buttonPressed={() => evidenceCardClicked(item)}
              programEvidence={item}
              program={program}
            />
          ))}
        </ProgramInfoCard>
        <ProgramInfoCard
          title="Received as supervisor"
          subTitle={
            wpbaFormSupervisorEvidence.length === 0
              ? "Any evidence you complete as a supervisor will appear below."
              : undefined
          }
        >
          {wpbaFormSupervisorEvidence.map((item) => (
            <ProgramEvidenceCard
              key={item.id}
              buttonPressed={() => evidenceCardClicked(item)}
              programEvidence={item}
              program={program}
            />
          ))}
        </ProgramInfoCard>
      </IonContent>
    </IonPage>
  );
};

export default WPBAPage;
